import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useMeetingAudio } from '@/features/meetings/hooks'
import { ApiError } from '@/internationalization/api-client'
import { PlayerContext, type Player, type PlayerState } from './PlayerContext'

/** How often the playhead is published to the tree. See PlayerContext. */
const PLAYHEAD_INTERVAL_MS = 250
export const SKIP_SECONDS = 10
const SPEEDS = [1, 1.25, 1.5, 2]

/**
 * Owns the `<audio>` element for one meeting and provides the player.
 *
 * WHY THE ELEMENT LIVES HERE AND NOT IN THE TRANSCRIPT. There was a player
 * once; it was removed, and its hook survived it — still building a real
 * `<audio>` with `preload="metadata"`, so opening the transcript tab fetched
 * the recording to drive a clock nothing played. Owning the element next to
 * the thing that plays is what stops that happening again: if this provider
 * is not mounted, nothing requests audio.
 *
 * WHY IT WRAPS THE WHOLE PAGE RATHER THAN A TAB. Playback has to survive
 * switching from the transcript to the summary and back. An `<audio>` inside
 * a tab is unmounted when the tab changes, which stops the audio and loses
 * the position — a player that stops when you look at the summary is one
 * nobody uses twice. The visible controls are `MeetingPlayerBar`, rendered
 * wherever the page wants them.
 *
 * THE SOURCE IS FETCHED, NOT LINKED. `src` is a presigned S3 URL obtained
 * through the authenticated client, because the access token is in memory and
 * an `<audio>` element cannot send a header. That URL is a BEARER CREDENTIAL
 * for the recording, so it is never rendered as a visible link, never logged,
 * and never persisted.
 */
export function MeetingPlayer({
  meetingId,
  /** False when the meeting cannot have audio — nothing is requested at all. */
  enabled,
  children,
}: {
  meetingId: string
  enabled: boolean
  children: ReactNode
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState<number | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [muted, setMuted] = useState(false)
  const [speed, setSpeed] = useState(1)
  const [mediaError, setMediaError] = useState(false)
  // A retry is in flight. See `retry` for why this is its own flag.
  const [refreshing, setRefreshing] = useState(false)

  const { data, error, refetch } = useMeetingAudio(meetingId, enabled)

  // THE PLAYHEAD IS POLLED RATHER THAN READ FROM `timeupdate`. That event
  // fires at a rate the spec does not pin down (~4 Hz in practice, less while
  // the tab is backgrounded), so the highlighted transcript line would lag
  // visibly on some browsers and not others. An interval that only runs while
  // something is playing costs nothing when nothing is.
  useEffect(() => {
    if (!isPlaying) return
    const id = window.setInterval(() => {
      const el = audioRef.current
      if (el) setCurrentTime(el.currentTime)
    }, PLAYHEAD_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [isPlaying])

  useEffect(() => {
    const el = audioRef.current
    if (el) el.playbackRate = speed
  }, [speed, data?.url])

  // A new URL (first load, or a refetch after the old one expired) resets the
  // element. The position has to survive that, or a rotating credential
  // throws a listener back to the start of a two-hour recording.
  const resumeAt = useRef(0)
  // Whether the LISTENER wants audio playing, as distinct from whether the
  // element currently is. Set where the intent is actually expressed rather
  // than mirrored off `isPlaying`, so a swap-induced pause cannot clear it.
  const wantsToPlay = useRef(false)
  useEffect(() => {
    resumeAt.current = currentTime
  }, [currentTime])

  // EVERY ONE OF THESE IS PER-MEETING, AND NOTHING REMOUNTS THIS COMPONENT.
  // The route is `/meetings/:meetingId` with a single element and `useMeeting`
  // serves a cached meeting instantly, so `QueryBoundary` never falls back to
  // its skeleton and the provider survives the navigation. Without this,
  // opening meeting B, playing to 10:00 and going back to a cached meeting A
  // starts A ten minutes in -- `onLoadedMetadata` restores `resumeAt` -- with
  // the transcript highlighting a line nobody is at; a media error on B makes
  // a healthy A render "the recording stopped loading"; and B's mute survives
  // onto an element that is not muted, so the icon lies.
  useEffect(() => {
    resumeAt.current = 0
    wantsToPlay.current = false
    setCurrentTime(0)
    setDuration(null)
    setIsPlaying(false)
    setMediaError(false)
    setRefreshing(false)
    setMuted(false)
    // THE STATE AND THE ELEMENT ARE TWO PLACES AND BOTH HAVE TO BE RESET.
    // `muted` is not a React-controlled attribute here -- the toggle sets
    // `el.muted` directly -- so `setMuted(false)` alone moves the ICON and
    // leaves the reused `<audio>` still muted. On a cached meeting that is
    // silent playback under an unmuted speaker icon, which reads as broken
    // audio rather than as a mute somebody left on in a different meeting.
    const el = audioRef.current
    if (el) el.muted = false
  }, [meetingId])

  const seek = useCallback((seconds: number) => {
    const el = audioRef.current
    if (!el) return
    const bounded = Math.max(
      0,
      Number.isFinite(el.duration) ? Math.min(seconds, el.duration) : seconds,
    )
    el.currentTime = bounded
    setCurrentTime(bounded)
  }, [])

  // `play()` REJECTS FOR REASONS THAT ARE NOT FAILURES. "The play() request
  // was interrupted by a call to pause()" and "...by a new load request" are
  // both AbortError and both entirely normal -- a fast play/pause, or a
  // presigned URL being swapped in the same tick. Treating them as a media
  // failure replaced the whole control bar with "The recording stopped
  // loading. Its link may have expired." on a perfectly healthy recording,
  // recoverable only by a retry round trip. NotAllowedError is the browser's
  // autoplay policy and is also not a broken source; the user pressing the
  // button again is the fix.
  const play = useCallback((el: HTMLAudioElement) => {
    wantsToPlay.current = true
    void el.play().catch((error: unknown) => {
      const name = error instanceof DOMException ? error.name : ''
      if (name !== 'AbortError' && name !== 'NotAllowedError') setMediaError(true)
    })
  }, [])

  const playFrom = useCallback(
    (seconds: number) => {
      seek(seconds)
      const el = audioRef.current
      if (el) play(el)
    },
    [seek, play],
  )

  const toggle = useCallback(() => {
    const el = audioRef.current
    if (!el) return
    if (el.paused) {
      play(el)
    } else {
      // The only place a pause is the LISTENER's. `onPause` is not: the media
      // load algorithm fires one every time `src` changes, so syncing the
      // intent from that event would clear it during the very swap it exists
      // to survive -- and `onPause` lands after this effect, so the resume
      // would never fire.
      wantsToPlay.current = false
      el.pause()
    }
  }, [play])

  // `refreshing` COMES FIRST. While a retry is in flight `data` still holds the
  // URL that just failed, so without it `state` read 'ready' the moment the
  // error flag cleared -- a player that looked playable on a dead source, and a
  // transcript that switched to following its frozen playhead.
  const state: PlayerState = refreshing
    ? 'loading'
    : mediaError
    ? 'media-error'
    : error
      ? errorState(error)
      : data
        ? 'ready'
        : 'loading'

  // NULL WHEN THIS MEETING CANNOT HAVE AUDIO, which is what keeps the bar off
  // a scheduled meeting entirely. With `enabled` false the query never runs,
  // so `data` and `error` both stay undefined and the state would sit on
  // `loading` forever -- a disabled play button and a spinner on a meeting
  // that has not happened yet. A null player also puts the transcript back on
  // its own clock, which is exactly right there.
  // STABLE ON PURPOSE: `TranscriptPanel` puts it in the deps of the callback it
  // hands to every memoised line, and a new identity per render would re-render
  // all of them each time the playhead publishes.
  //
  // THE ERROR FLAG IS CLEARED WHEN THE REFETCH SETTLES, NOT WHEN IT STARTS, and
  // `seconds` is stored before anything else so the requested moment survives
  // the `src` swap: `onLoadedMetadata` restores `resumeAt`, and `currentTime`
  // is set now so the transcript highlight sits on the requested line during
  // the swap instead of on the old playhead.
  const retry = useCallback(
    async (seconds?: number): Promise<boolean> => {
      if (seconds !== undefined) {
        resumeAt.current = seconds
        wantsToPlay.current = true
        setCurrentTime(seconds)
      }
      setRefreshing(true)
      try {
        const result = await refetch()
        return !result.isError
      } catch {
        return false
      } finally {
        setMediaError(false)
        setRefreshing(false)
      }
    },
    [refetch],
  )

  const player = useMemo<Player | null>(
    () => (!enabled ? null : {
      currentTime,
      duration,
      isPlaying,
      seek,
      playFrom,
      toggle,
      isReady: state === 'ready',
      state,
      muted,
      speed,
      skip: (delta: number) => seek(currentTime + delta),
      toggleMute: () => {
        const el = audioRef.current
        if (!el) return
        el.muted = !el.muted
        setMuted(el.muted)
      },
      cycleSpeed: () =>
        setSpeed((current) => SPEEDS[(SPEEDS.indexOf(current) + 1) % SPEEDS.length]),
      retry,
    }),
    [enabled, currentTime, duration, isPlaying, seek, playFrom, toggle, state,
     muted, speed, retry],
  )

  return (
    <PlayerContext.Provider value={player}>
      {data?.url && (
        /* No <track>: the transcript beside this IS the caption track, and a
           real one would need a WebVTT artefact nothing in the pipeline
           writes. */
        <audio
          ref={audioRef}
          src={data.url}
          // `metadata`, not `auto`. `auto` starts pulling a ~115 MB WAV the
          // moment the page opens, for every meeting anybody looks at, most
          // of which nobody listens to.
          preload="metadata"
          onLoadedMetadata={(event) => {
            const el = event.currentTarget
            el.playbackRate = speed
            setDuration(Number.isFinite(el.duration) ? el.duration : null)
            if (resumeAt.current > 0 && resumeAt.current < el.duration) {
              el.currentTime = resumeAt.current
            }
          }}
          onCanPlay={(event) => {
            // A SWAPPED `src` PAUSES THE ELEMENT. `refetchOnWindowFocus`
            // hands us a fresh presigned URL roughly every time somebody
            // comes back to the tab, and changing `src` runs the media load
            // algorithm: it pauses, resets, and `onLoadedMetadata` restores
            // the position -- but nothing resumed. Playback stopped dead
            // mid-sentence with the button flipped to Play and no
            // explanation. `wasPlaying` carries the intent across the swap.
            if (wantsToPlay.current && event.currentTarget.paused) {
              play(event.currentTarget)
            }
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={(event) => {
            // The interval is the only writer WHILE playing and is torn down
            // by this state change, so without a final read the playhead
            // freezes up to one tick short -- a recording played to the end
            // reads 59:59 / 1:00:00, and a skip taken after a pause computes
            // from a stale position.
            setCurrentTime(event.currentTarget.currentTime)
            setIsPlaying(false)
          }}
          onEnded={(event) => {
            wantsToPlay.current = false
            setCurrentTime(event.currentTarget.currentTime)
            setIsPlaying(false)
          }}
          // An expired URL and an object S3 refused both surface here as one
          // opaque media error, so the recovery is the same for both: sign a
          // new URL, and say so if that fails too.
          onError={() => setMediaError(true)}
          onSeeked={(event) => setCurrentTime(event.currentTarget.currentTime)}
          className="hidden"
        />
      )}
      {children}
    </PlayerContext.Provider>
  )
}

function errorState(error: unknown): PlayerState {
  if (error instanceof ApiError) {
    if (error.status === 404) return 'unavailable'
    if (error.status === 503) return 'not-configured'
    // A REFUSAL IS NOT A FAULT. Falling through to 'failed' told every member
    // and viewer in the workspace that the recording could not be loaded, and
    // offered them a retry, when what had actually happened was that their
    // role did not hold `audio:download`. See PlayerState.
    if (error.status === 403) return 'forbidden'
  }
  return 'failed'
}
